import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert as MuiAlert } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import SaveIcon from '@mui/icons-material/Save';
import Snackbar from '@mui/material/Snackbar';
import { AuditLoggers } from '../../../utils/auditLogger';
import RoleFunctionalPermissions from './RoleFunctionalPermissions';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { FC } from '../../../constants/functionCodes';

/**
 * Display names match core.functions.name 1:1 after migration.
 */
const getFunctionDisplayName = (functionName) => functionName;

/** Hidden from Tenant Role User Rights grid (menus already removed from sidebar). */
const HIDDEN_FROM_RIGHTS_SCREEN = [
  FC.DEBT_COLLECTION,
  FC.ACCOUNTS_PAYABLE,
  FC.ANOMALY_DETECTION,
  FC.TREASURY,
  FC.AP_INVOICE,
  FC.CHARGEBACK_MANAGEMENT,
];

const getFunctionName = (func) => func?.name || func?.functionName || func?.function || '';

const isHiddenFromRightsScreen = (functionName) => {
  const name = String(functionName || '').trim().toLowerCase();
  if (!name) return false;
  if (HIDDEN_FROM_RIGHTS_SCREEN.some((item) => String(item || '').toLowerCase() === name)) {
    return true;
  }
  // Legacy DB row names (see migrate_menu_function_codes_1to1.sql)
  return name === 'ap invoice'
    || name.includes('legacy ap invoice')
    || name === 'chargeback'
    || name === 'chargeback management'
    || name === 'work_flow_upload'
    || name === 'work_flow_data_edit'
    || name === 'work_flow_step_view';
};

const CustomAlert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UserRights = ({ open, onClose, tenantRoleId, roleName: propRoleName }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRights, setUserRights] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState('userRights');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [mergedData, setMergedData] = useState([]);

  const fetchFunctions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/functions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch functions');
      const data = await resp.json();
      const list = Array.isArray(data) ? data : [];
      const visible = list.filter((func) => !isHiddenFromRightsScreen(getFunctionName(func)));
      const homeRows = visible.filter((func) => getFunctionName(func) === FC.HOME);
      const otherRows = visible.filter((func) => getFunctionName(func) !== FC.HOME);
      setFunctions([...homeRows, ...otherRows]);
    } catch (err) {
      console.error('Error fetching functions:', err);
      setFunctions([]);
    }
  };

  const fetchUserRights = async () => {
    if (!tenantRoleId) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/rights/tenant/${tenantId}/role/${tenantRoleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch user rights');
      const data = await resp.json();
      setUserRights(Array.isArray(data) ? data : []);
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
      setUserRights([]);
    } finally {
      setLoading(false);
    }
  };

  // Merge functions and rights data
  const mergeData = () => {
    const merged = functions.map(func => {
      // Find existing right for this function
      const existingRight = userRights.find(right => right.functionId === func.functionId);
      
      if (existingRight) {
        // Use existing right data
        return {
          id: existingRight.id,
          rightId: existingRight.id,
          functionId: func.functionId,
          function: func.name,
          enable: existingRight.enable || false,
          upload: existingRight.upload || false,
          view: existingRight.view || false,
          add: existingRight.add || false,
          edit: existingRight.edit || false,
          delete: existingRight.delete || false,
          export: existingRight.export || false,
          isNew: false // This right already exists in the database
        };
      } else {
        // Create new right entry with default values
        return {
          id: `new_${func.functionId}`, // Temporary ID for new entries
          rightId: null, // No existing right ID
          functionId: func.functionId,
          function: func.name,
          enable: false,
          upload: false,
          view: false,
          add: false,
          edit: false,
          delete: false,
          export: false,
          isNew: true // This is a new right that needs to be created
        };
      }
    });
    
    setMergedData(merged);
  };

  useEffect(() => {
    if (!open || !tenantRoleId) return;
    fetchFunctions();
    fetchUserRights();
  }, [open, tenantRoleId]);

  // Merge data when both functions and userRights are loaded
  useEffect(() => {
    if (functions.length > 0) {
      mergeData();
    }
  }, [functions, userRights]);

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    setSelectedUser('');
    
    // Fetch available users for dropdown
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch users');
      const data = await resp.json();
      setAvailableUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setAvailableUsers([]);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/user-rights`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantRoleId: tenantRoleId,
          userId: selectedUser,
        }),
      });
      
      if (!resp.ok) throw new Error('Failed to add user right');
      handleCloseDialog();
      fetchUserRights();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRightChange = (itemId, field, value) => {
    setMergedData(prevData => 
      prevData.map(item => 
        item.id === itemId 
          ? { ...item, [field]: value }
          : item
      )
    );
    setHasChanges(true);
  };

  const PERMISSION_FIELDS = ['enable', 'upload', 'view', 'add', 'edit', 'delete', 'export'];

  const checkboxSx = {
    color: ht.line,
    p: 0.5,
    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
      color: ht.green,
    },
  };

  /** Toggle every permission on a single function row */
  const handleRowSelectAll = (itemId, checked) => {
    setMergedData((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? PERMISSION_FIELDS.reduce((acc, field) => ({ ...acc, [field]: checked }), { ...item })
          : item
      )
    );
    setHasChanges(true);
  };

  const getRowSelectState = (row) => {
    const enabledCount = PERMISSION_FIELDS.filter((field) => row[field]).length;
    return {
      checked: enabledCount === PERMISSION_FIELDS.length,
      indeterminate: enabledCount > 0 && enabledCount < PERMISSION_FIELDS.length,
    };
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found in localStorage');
      
      // Transform mergedData to match the API structure
      // Only include items that have at least one permission enabled or are existing rights
      const bulkUpdateData = mergedData
        .filter(item => 
          // Include if it's an existing right OR if it has at least one permission enabled
          !item.isNew || item.enable || item.upload || item.view || item.add || item.edit || item.delete || item.export
        )
        .map(item => ({
          tenantId: tenantId,
          roleId: tenantRoleId,
          functionId: item.functionId,
          enable: item.enable || false,
          upload: item.upload || false,
          view: item.view || false,
          add: item.add || false,
          edit: item.edit || false,
          delete: item.delete || false,
          export: item.export || false
        }));
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/rights/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bulkUpdateData),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save user rights: ${resp.status}`);
      }
      
      const result = await resp.json();
      
      // Log successful rights update
      const tenantName = localStorage.getItem('tenant_name') || 'Unknown Tenant';
      await AuditLoggers.userRightsUpdated(
        tenantRoleId,
        propRoleName || 'Unknown Role',
        tenantName,
        bulkUpdateData.length
      ).catch(err => console.error('Error logging audit trail:', err));
      
      setHasChanges(false);
      
      // Show success message using snackbar
      showSnackbar('User rights saved successfully!', 'success');
      
      // Refresh the data to ensure consistency
      await fetchUserRights();
      
    } catch (err) {
      console.error('Error saving user rights:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save user rights';
      setError(errorMessage);
      // Show error message using snackbar
      showSnackbar(`Error: ${errorMessage}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const createCheckboxColumn = (field, headerName) => ({
    field,
    headerName,
    flex: 1,
    minWidth: 80,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params) => (
      <Checkbox
        checked={!!params.value}
        onChange={(e) => handleRightChange(params.row.id, field, e.target.checked)}
        size="small"
        sx={checkboxSx}
      />
    ),
  });

  const columns = [
    {
      field: 'function',
      headerName: 'Function',
      flex: 2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const rowState = getRowSelectState(params.row);
        const displayName = getFunctionDisplayName(params.row.function);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', pl: 0.5 }}>
            <Checkbox
              size="small"
              checked={rowState.checked}
              indeterminate={rowState.indeterminate}
              onChange={(e) => handleRowSelectAll(params.row.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              sx={checkboxSx}
              inputProps={{ 'aria-label': `Select all rights for ${displayName}` }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: ht.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </Typography>
          </Box>
        );
      },
    },
    createCheckboxColumn('enable', 'Enable'),
    createCheckboxColumn('upload', 'Upload'),
    createCheckboxColumn('view', 'View'),
    createCheckboxColumn('add', 'Add'),
    createCheckboxColumn('edit', 'Edit'),
    createCheckboxColumn('delete', 'Delete'),
    createCheckboxColumn('export', 'Export'),
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 900, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Tenant Role Details
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'userRights'}
          onChange={() => setExpanded(expanded === 'userRights' ? false : 'userRights')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>User Rights</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasChanges && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveAll();
                  }}
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ ...harmonateProceedButtonSx, mr: 1 }}
                >
                  {saving ? 'Saving...' : 'Save All'}
                </Button>
              )}
              {/* <IconButton 
                onClick={e => { 
                  e.stopPropagation(); 
                  handleOpenDialog(); 
                }} 
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton> */}
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 400,
            overflow: 'hidden',
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress sx={{ color: ht.blue }} />
              </Box>
            ) : error ? (
              <MuiAlert severity="error">{error}</MuiAlert>
            ) : (
              <Box sx={{ 
                flex: 1, 
                minHeight: 0, 
                display: 'flex', 
                flexDirection: 'column', 
                height: 320 
              }}>
                <DataGridPro
                  columnResizeMode="onEnd"
                  resizable
                  rows={mergedData}
                  rowHeight={35}
                  columns={columns}
                  disableSelectionOnClick
                  sx={{ 
                    ...harmonateDataGridSx,
                    flex: 1,
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.75rem',
                      color: ht.text,
                      bgcolor: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell[data-field="function"]': {
                      justifyContent: 'flex-start',
                    },
                  }}
                  getRowId={(row) => row.id}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>No functions found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <RoleFunctionalPermissions
          tenantRoleId={tenantRoleId}
          open={open}
          onSaved={(message) => showSnackbar(message, 'success')}
          onError={(message) => showSnackbar(message, 'error')}
        />
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add User Right</DialogTitle>
        <form onSubmit={handleAddUser}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              select
              label="User"
              name="user"
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              {availableUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting || !selectedUser} 
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Adding...' : 'Add User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <CustomAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </CustomAlert>
      </Snackbar>
    </Drawer>
  );
};

export default UserRights;